<template>
  <div>
    <slot :items="list" :unloaded-items="unloadedItems" />
    <infinite-loading
      v-if="shouldFetch && !isEmpty"
      ref="inf"
      spinner="waveDots"
      :force-use-infinite-wrapper="containerSelector"
      :identifier="infiniteId"
      :direction="direction"
      @infinite="infiniteHandler"
    >
      <div slot="no-more"></div>
      <div slot="no-results"><slot name="no-results"></slot></div>
      <div v-if="customSpinner" slot="spinner"></div>
    </infinite-loading>
    <div v-if="isEmpty"><slot name="no-results"></slot></div>
  </div>
</template>

<script>
import InfiniteLoading from 'vue-infinite-loading';
import isEqual from 'lodash.isequal';

import api from '@/api';
import apiV2 from '@/api/v2';

export default {
  components: {
    InfiniteLoading,
  },
  props: {
    url: {
      type: String,
      required: true,
    },
    query: {
      type: Object,
      default: () => ({}),
    },
    containerSelector: {
      type: String,
      default: '',
    },
    startPage: {
      type: Number,
      default: 1,
    },
    limit: {
      type: Number,
      default: 10,
    },
    enableQueryWatcher: {
      type: Boolean,
      default: false,
    },
    customSpinner: {
      type: Boolean,
      default: false,
    },
    direction: {
      type: String,
      default: 'bottom',
    },
    shouldFetch: {
      type: Boolean,
      default: true,
    },
    forceUpdate: {
      type: [Boolean, Number],
      default: false,
    },
    apiV2: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      page: this.startPage,
      list: [],
      isEmpty: false,
      queue: [],
      state: null,
      infiniteId: +new Date(),
      unloadedItems: null,
    };
  },
  computed: {
    apiInstance() {
      return this.apiV2 ? apiV2 : api;
    },
  },
  watch: {
    async query(value, oldValue) {
      if (this.enableQueryWatcher && !isEqual(value, oldValue)) {
        await this.reload();
      }
    },
    forceUpdate(val) {
      if (val) this.reload();
    },
    async url() {
      await this.reload();
    },
  },
  async mounted() {
    await this.infiniteHandler();
    this.$emit('data-deliver', this.list);
  },
  methods: {
    getUnloadedItems(data) {
      const totalCount = this.apiV2 ? data.totalCount : data.pagination.total;
      const currentlyLoaded = this.list.length;
      return Math.max(totalCount - currentlyLoaded, 0);
    },

    async infiniteHandler($state) {
      if ($state) this.state = $state;

      if (this.queue[this.queue.length - 1] !== this.page) {
        this.queue.push(this.page);

        try {
          const { data } = await this.apiInstance.get(this.url, {
            params: {
              page: this.page,
              ...(this.apiV2 ? { perPage: this.limit } : { limit: this.limit }),
              ...this.query,
            },
          });

          const results = this.apiV2 ? data.nodes : data.results;

          if (results.length) {
            if (this.page === this.startPage) {
              this.direction === 'top'
                ? (this.list = results.reverse())
                : (this.list = results);
            } else {
              this.direction === 'top'
                ? this.list.unshift(...results.reverse())
                : this.list.push(...results);
            }

            if (this.state) this.state.loaded();
            this.page++;

            const totalCount = this.apiV2
              ? data.totalCount
              : data.pagination.total;

            if (this.list.length >= totalCount) {
              if (this.state) this.state.complete();
            }

            this.$emit('update', this.list);
          } else {
            if (this.page === 1) {
              this.list = [];
              this.isEmpty = true;
            }
            if (this.state) this.state.complete();
          }

          this.unloadedItems = this.getUnloadedItems(data);
        } catch (error) {
          if (this.state) this.state.error();
        }
      }
    },

    async reload() {
      this.isEmpty = false;
      this.page = this.startPage;
      this.infiniteId += 1;
      this.queue = [];
      this.list = [];

      this.$emit('data-deliver', this.list);
    },
  },
};
</script>
